import { Component, useState, useEffect } from 'react'
import Select from 'ol/interaction/Select'
import Collection from 'ol/Collection'
import * as condition from 'ol/events/condition'
import GeoJSON from 'ol/format/GeoJSON'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Circle from 'ol/style/Circle'
import Control from 'ol/control/Control'
import { colors } from '../../ol-styles'
import { createPortal } from 'react-dom'
import ToolbarButton from '../ui/shared/toolbar-button'
import Draw from 'ol/interaction/Draw'
import DragBox from 'ol/interaction/DragBox'
import booleanWithin from '@turf/boolean-within'

import PropTypes from 'prop-types'

export default class PreviewMapFeatureSelect extends Component {
  constructor(props) {
    super(props)
    this.styleFunction = this.styleFunction.bind(this)
    this.interactions = []
    this.state = {
      selectionMode: 'move',
    }
  }

  componentDidMount() {
    if (this.props.enabled) {
      this.updateSelect()
    }

    this.registerInteraction(
      new DragBox({
        condition: e =>
          this.state.selectionMode === 'rectangle' || condition.shiftKeyOnly(e),
        className: 'zonal-statistics-dragbox',
        minArea: 256,
      }),
      {
        boxend: e => {
          this.selectIntersecting(e.target.getGeometry())
          this.setState({ selectionMode: 'move' })
        },
      }
    )

    this.registerInteraction(
      new Draw({
        type: 'Polygon',
        condition: () => this.state.selectionMode === 'polygon',
        style: new Style({
          fill: new Fill({ color: [255, 255, 255, 0.1] }),
          stroke: new Stroke({ color: '#00a6cc', width: 1, lineDash: [1, 1] }),
        }),
      }),
      {
        drawend: e => {
          this.selectIntersecting(e.feature.getGeometry())
          this.setState({ selectionMode: 'move' })
        },
      },
      'polygon'
    )

    this.interactions.forEach(i => this.props.map.addInteraction(i[0]))
  }

  componentWillUnmount() {
    this.props.map.removeInteraction(this.select)
    this.interactions.forEach(i => this.props.map.removeInteraction(i[0]))
  }

  componentDidUpdate(oldProps, oldState) {
    if (
      oldProps.selection !== this.props.selection ||
      oldProps.layer !== this.props.layer ||
      oldProps.lastSelected !== this.props.lastSelected
    ) {
      if (this.props.enabled) {
        this.updateSelect()
      } else {
        this.props.map.removeInteraction(this.select)
      }
    }

    if (oldState.selectionMode !== this.state.selectionMode) {
      this.select.setActive(this.state.selectionMode === 'move')
      this.interactions.forEach(
        i => i[1] && i[0].setActive(this.state.selectionMode === i[1])
      )
    }
  }

  render() {
    return (
      <>
        <SelectionModeToolbar
          map={this.props.map}
          mode={this.state.selectionMode}
          enabled={this.props.enabled}
          onChange={selectionMode => this.setState({ selectionMode })}
        />
      </>
    )
  }

  styleFunction(feature) {
    if (this.props.lastSelected) {
      const isLastSelected = feature.getId() === this.props.lastSelected
      if (isLastSelected) {
        return this.props.selection.has(this.props.lastSelected)
          ? selectedPreviewStyle
          : previewStyle
      }
    }

    return normalStyle
  }

  updateSelect() {
    const allSelections = (
      (this.props.layer &&
        this.props.layer.getSource() &&
        this.props.layer.getSource().getFeatures()) ||
      []
    ).filter(
      feature =>
        this.props.selection.has(feature.getId()) ||
        this.props.lastSelected === feature.getId()
    )

    if (this.select) {
      this.props.map.removeInteraction(this.select)
    }

    this.select = new Select({
      layers: [this.props.layer],
      condition: evt =>
        this.state.selectionMode === 'move' && condition.click(evt),
      toggleCondition: evt => condition.click(evt),
      multi: true,
      style: this.styleFunction,
      features: new Collection(allSelections),
    })

    this.select.on('select', e => {
      if (e.deselected.length !== 0) {
        const featuresGeoJson = new GeoJSON().writeFeaturesObject(
          e.deselected,
          {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          }
        )
        this.props.onDeselect(featuresGeoJson)
        return
      }

      const featuresGeoJson = new GeoJSON().writeFeaturesObject(e.selected, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      })
      this.props.onSelect(featuresGeoJson)
    })

    this.props.map.addInteraction(this.select)
  }

  registerInteraction(interaction, eventHandlers, mode) {
    Object.keys(eventHandlers).forEach(eventName =>
      interaction.on(eventName, eventHandlers[eventName])
    )
    this.interactions.push([interaction, mode])

    if (mode) {
      interaction.setActive(this.state.selectionMode === mode)
    }

    return interaction
  }

  selectIntersecting(geometry) {
    const extent = geometry.getExtent()
    const geojson = new GeoJSON()
    const gjGeometry = geojson.writeGeometryObject(geometry)
    const features = []
    this.props.layer.getSource().forEachFeatureIntersectingExtent(extent, f => {
      const feature = geojson.writeFeatureObject(f)
      if (booleanWithin(feature, gjGeometry)) {
        features.push(feature)
      }
    })

    if (features.length > 0) {
      this.props.onSelect({ type: 'FeatureCollection', features })
    }
  }
}

const selectedPreviewStyle = new Style({
  image: new Circle({
    radius: 10,
    fill: new Fill({ color: colors.blue }),
    stroke: new Stroke({ color: colors.red, width: 4 }),
  }),
})

const previewStyle = new Style({
  image: new Circle({
    radius: 10,
    fill: new Fill({ color: colors.lightGray }),
    stroke: new Stroke({ color: colors.red, width: 4 }),
  }),
})

const normalStyle = new Style({
  image: new Circle({
    radius: 6,
    fill: new Fill({ color: colors.blue }),
    stroke: new Stroke({ color: colors.white, width: 2 }),
  }),
})

function SelectionModeToolbar({ map, mode, enabled, onChange }) {
  const [containerElement, setContainerElement] = useState(null)

  useEffect(() => {
    const controlElement = document.createElement('div')
    const controls = new Control({
      target: document.getElementById('map-controls'),
      element: controlElement,
    })

    map.addControl(controls)

    setContainerElement(controlElement)

    return () => {
      map.removeControl(controls)
    }
  }, [])

  const modes = [
    ['Select Point', 'dot-circle', 'move'],
    ['Select Rectangle', 'object-group', 'rectangle'],
    ['Select Polygon', 'draw-polygon', 'polygon'],
  ]

  return (
    (containerElement &&
      createPortal(
        <div className="tw-p-2" style={{ pointerEvents: 'auto' }}>
          {modes.map(([title, icon, modeName]) => (
            <ToolbarButton
              key={modeName}
              title={title}
              icon={icon}
              active={mode === modeName}
              disabled={!enabled}
              onClick={() => {
                onChange(modeName)
              }}
            />
          ))}
        </div>,
        containerElement
      )) ||
    null
  )
}

PreviewMapFeatureSelect.propTypes = {
  enabled: PropTypes.bool,
  map: PropTypes.shape({
    addInteraction: PropTypes.func,
    removeInteraction: PropTypes.func,
  }),
  selection: PropTypes.object,
  layer: PropTypes.object,
  lastSelected: PropTypes.string,
  onDeselect: PropTypes.func,
  onSelect: PropTypes.func,
}
